import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import { createScrap, updateScrap } from "../../../redux/action/scraps";

// Enum values kept in sync with the server (contracts/validators/scraps.js)
export const UOM_OPTIONS = [
  "KG",
        "TON",
        "PIECE",
        "NO",
        "M3",
        "CM3",
        "MM3",
        "SQM",
        "LITER",
        "BAG",
        "BUNDLE",
        "ROLL",
        "DRUM",
        "BOX",
        "METER",
        "SQFT",
        "CU.M",
        "FT",
        "YARD",
        "GALLON",
        "PALLET",
        "SET",
        "PACK",
        "BOTTLE",
        "CAN",
        "TUBE",
        "CARTON",
        "SACK",
        "CRATE",
        "BARREL",
        "SET",
        "REEL",
        "COIL",
        "M",
        "L",
        "ML",
        "OZ",
        "GM",
        "G",
        "LB",
        "IN",
        "YD",
        "SQ.IN",
        "SQ.YD",
        "CU.IN",
        "CU.YD",
        "F12",
        "F24",
        "F36",
        "F48",
        "F60",
        "F72",
        "F84",
        "F96",
        "F108",
        "F120",
];

export const SCRAP_TYPE_OPTIONS = [
  "Metal",
  "Plastic",
  "Paper",
  "Cardboard",
  "Glass",
  "Fabric & Textiles",
  "Rubber",
  "Leather",
  "Wood",
  "Electronics (E-Waste)",
  "Batteries",
  "Cable & Wire",
  "Chemical Waste",
  "Oil & Lubricants",
  "Paint & Ink Waste",
  "Biomedical Waste",
  "Construction & Demolition",
  "Organic Waste",
  "Food Waste",
  "Agricultural Waste",
  "Packaging Waste",
  "Industrial Sludge",
  "Ceramic & Tiles",
  "Foam & Sponge",
  "Composite Materials",
  "Tyres",
  "Fluorescent & Lighting Waste",
  "Water Treatment Waste",
  "Mining Waste",
  "Automotive Waste",
  "Marine Waste",
  "Household Waste",
  "Mixed Dry Waste",
  "Confidential/Document Shredding",
  "Rejected/Expired Products",
  "Ash & Dust",
];

export const DISPOSAL_ROUTE_OPTIONS = [
  "Recycling",
  "Reuse",
  "Landfill",
  "Treatment",
  "Waste-to-Energy",
  "Co-Processing",
  "Incineration",
  "Composting",
  "Anaerobic Digestion",
  "Refurbishment",
  "Recovery",
  "Secure Disposal",
];

export const CLASSIFICATION_OPTIONS = ["Hazardous", "Non-Hazardous"];

const emptyForm = {
  name: "",
  variant: "",
  type: "",
  uom: "KG",
  disposalRoute: "",
  classification: "",
  articleCode: "",
  hsnCode: "",
  description: "",
  termsAndConditions: "",
};

const labelStyle = { fontSize: 13, fontWeight: 600, color: "#475467" };
const fieldStyle = {
  border: "1px solid #D0D5DD",
  borderRadius: 8,
  padding: "8px 10px",
  height: 42,
  margin: 0,
  width: "100%",
  background: "#fff",
};

// Form used both to create a new scrap and to edit an existing one.
// `scrap` = the scrap being edited (null when creating).
// `onSaved(scrap)` fires after a successful save so the parent can link it.
const AddScrapModal = ({ scrap, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const { savingScrap } = useSelector((state) => state.scraps);
  const isEdit = Boolean(scrap?._id);

  const [form, setForm] = useState(() =>
    scrap
      ? {
          name: scrap.name || "",
          variant: scrap.variant || "",
          type: scrap.type || "",
          uom: scrap.uom || "KG",
          disposalRoute: scrap.disposalRoute || "",
          classification: scrap.classification || "",
          articleCode: scrap.articleCode || "",
          hsnCode: scrap.hsnCode || "",
          description: scrap.description || "",
          termsAndConditions: scrap.termsAndConditions || "",
        }
      : { ...emptyForm }
  );
  const [errors, setErrors] = useState({});

  const setField = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (!form.variant.trim()) next.variant = "Type / grade is required";
    if (!form.type) next.type = "Category is required";
    if (!form.uom) next.uom = "Unit is required";
    if (!form.disposalRoute) next.disposalRoute = "Disposal route is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // trim / drop empties so the server keeps clean data
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      const val = typeof v === "string" ? v.trim() : v;
      if (val !== "") payload[k] = val;
    });

    if (isEdit) {
      dispatch(
        updateScrap(scrap._id, payload, (data) => {
          onSaved && onSaved(data);
          onClose && onClose();
        })
      );
    } else {
      dispatch(
        createScrap(payload, (data) => {
          onSaved && onSaved(data);
          onClose && onClose();
        })
      );
    }
  };

  const errText = (key) =>
    errors[key] ? (
      <span style={{ color: "#d92d20", fontSize: 12 }}>{errors[key]}</span>
    ) : null;

  return (
    <form onSubmit={submit} style={{ minWidth: 320 }}>
      <h5 className="bold" style={{ marginTop: 0 }}>
        {isEdit ? "Edit Scrap" : "Add Scrap"}
      </h5>

      <div className="flex flex-wrap" style={{ gap: 14 }}>
        {/* Name */}
        <div className="flex column" style={{ flex: "1 1 45%", gap: 4 }}>
          <label style={labelStyle}>Scrap Name *</label>
          <input
            className="browser-default"
            style={fieldStyle}
            value={form.name}
            onChange={setField("name")}
            placeholder="e.g. EOL Waste Tyre"
          />
          {errText("name")}
        </div>

        {/* Variant */}
        <div className="flex column" style={{ flex: "1 1 45%", gap: 4 }}>
          <label style={labelStyle}>Type / Grade *</label>
          <input
            className="browser-default"
            style={fieldStyle}
            value={form.variant}
            onChange={setField("variant")}
            placeholder="e.g. Truck Bus Radial"
          />
          {errText("variant")}
        </div>

        {/* Category / type */}
        <div className="flex column" style={{ flex: "1 1 45%", gap: 4 }}>
          <label style={labelStyle}>Category *</label>
          <select
            className="browser-default"
            style={fieldStyle}
            value={form.type}
            onChange={setField("type")}
          >
            <option value="">Select category</option>
            {SCRAP_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {errText("type")}
        </div>

        {/* UOM */}
        <div className="flex column" style={{ flex: "1 1 45%", gap: 4 }}>
          <label style={labelStyle}>Unit (UOM) *</label>
          <select
            className="browser-default"
            style={fieldStyle}
            value={form.uom}
            onChange={setField("uom")}
          >
            {UOM_OPTIONS.map((u) => (
              <option key={u} value={u}>
                {u.toUpperCase()}
              </option>
            ))}
          </select>
          {errText("uom")}
        </div>

        {/* Disposal route */}
        <div className="flex column" style={{ flex: "1 1 45%", gap: 4 }}>
          <label style={labelStyle}>Disposal Route *</label>
          <select
            className="browser-default"
            style={fieldStyle}
            value={form.disposalRoute}
            onChange={setField("disposalRoute")}
          >
            <option value="">Select disposal route</option>
            {DISPOSAL_ROUTE_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          {errText("disposalRoute")}
        </div>

        {/* Classification */}
        <div className="flex column" style={{ flex: "1 1 45%", gap: 4 }}>
          <label style={labelStyle}>Classification</label>
          <select
            className="browser-default"
            style={fieldStyle}
            value={form.classification}
            onChange={setField("classification")}
          >
            <option value="">Not specified</option>
            {CLASSIFICATION_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Article code */}
        <div className="flex column" style={{ flex: "1 1 45%", gap: 4 }}>
          <label style={labelStyle}>Article Code</label>
          <input
            className="browser-default"
            style={fieldStyle}
            value={form.articleCode}
            onChange={setField("articleCode")}
            placeholder="Optional"
          />
        </div>

        {/* HSN code */}
        <div className="flex column" style={{ flex: "1 1 45%", gap: 4 }}>
          <label style={labelStyle}>HSN Code</label>
          <input
            className="browser-default"
            style={fieldStyle}
            value={form.hsnCode}
            onChange={setField("hsnCode")}
            placeholder="Optional"
          />
        </div>

        {/* Description */}
        <div className="flex column" style={{ flex: "1 1 100%", gap: 4 }}>
          <label style={labelStyle}>Description</label>
          <textarea
            className="browser-default"
            style={{ ...fieldStyle, height: 70, paddingTop: 8 }}
            value={form.description}
            onChange={setField("description")}
            placeholder="Optional"
          />
        </div>

        {/* Terms */}
        <div className="flex column" style={{ flex: "1 1 100%", gap: 4 }}>
          <label style={labelStyle}>Terms &amp; Conditions</label>
          <textarea
            className="browser-default"
            style={{ ...fieldStyle, height: 70, paddingTop: 8 }}
            value={form.termsAndConditions}
            onChange={setField("termsAndConditions")}
            placeholder="Optional"
          />
        </div>
      </div>

      <div
        className="flex align-center"
        style={{ justifyContent: "flex-end", gap: 10, marginTop: 18 }}
      >
        <button
          type="button"
          className="btn-small white purple-text"
          style={{ border: "1px solid #6F2DA8" }}
          onClick={onClose}
          disabled={savingScrap}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-small"
          style={{ background: "var(--primary-color)", border: "none" }}
          disabled={savingScrap}
        >
          {savingScrap ? (
            <ClipLoader size={18} color="#fff" />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Add Scrap"
          )}
        </button>
      </div>
    </form>
  );
};

export default AddScrapModal;
